const _ = require('lodash');
const { FILTER, FILTER_TYPES, GLOBAL_FILTERS, SELECTED_EXCLUDED, TOUCH_EXCLUDED, TOP_GROUPING, TOP_GROUPING_MAP, DISPLAY_TYPES } = require('../../config/filters');
const { STATIC_FILTERS_MAPPING_SEPARATORS, PAIR_FILTERS_IDS_FLP, MULTIPLE_FILTERS_IDS_FLP } = require('../constants');
const { splitArray } = require('../../utils');
const handler = require('./handlers');
const selectedHandler = require('./selected_handlers');
const { sortByOrderAscending, parseActiveFilters, activeFiltersToQueryString, sortByAliasAlphabetical } = require('./_utils');

const refineResults = [FILTER.SUB_CATEGORIES.ID, FILTER.NESTED_SUB_CATEGORIES.ID];

const buildAppFilters = (facets, context, flow, gigs) => {
    const { staticFilters, activeFilters, isTouch } = context;
    const noResults = !gigs.length;
    const baseStaticFilterKeys = Object.keys(staticFilters.active);
    const isMultipleFilterActive = !!_.intersection(baseStaticFilterKeys, MULTIPLE_FILTERS_IDS_FLP).length;
    const activeStaticFilterKeys = isMultipleFilterActive ? extractActiveStaticFilters(baseStaticFilterKeys, staticFilters) : baseStaticFilterKeys;

    facets = noResults ? buildFacetsFromActiveFilters(activeFilters) : facets;

    let mainFilters = buildMainFilters(facets, context, flow)
        .filter((sideBarFilter) => !activeStaticFilterKeys.includes(sideBarFilter.id))
        .filter((sideBarFilter) => !isTouch || !TOUCH_EXCLUDED.includes(sideBarFilter.id));

    if (noResults) {
        mainFilters = removeIfNoResults(activeFilters, mainFilters);
    }

    const subCategory = buildSubCategoryFilter(facets, context);

    const topBarFilters = mainFilters.filter((topFilter) => !!topFilter);
    const reorderedTopBarFilters = reorderFilters(topBarFilters);
    const topBarFiltersResult = manipulateTopFilters(reorderedTopBarFilters);
    const extraSubCategoryData = { id: context.dominateSubCategoryId, name: context.subCategoryName };
    const topBarOrderMap = topBarFiltersResult.reduce((total, filter, index) => {
        total[filter.id] = index;
        return total;
    }, {});

    const selected = buildSelectedFilter(mainFilters, topBarOrderMap, context);

    const appFilters = {
        sideBar: mainFilters,
        topBar: topBarFiltersResult,
        subCategory: { ...subCategory, ...extraSubCategoryData },
        activeFiltersQueryString: context.refcpa,
        selected
    };

    return context.abTestService.manipulateFiltersByExperiments(context, appFilters);
};

const buildMainFilters = (facets, context, flow) => {
    const facetArray = Object.entries(facets);
    if (facetArray.length === 0) {
        return [];
    }

    return facetArray
        .filter(([filter]) => !refineResults.includes(filter))
        .map(([filter, facetValues]) => handler[filter] ? handler[filter](context, facetValues) : handler.customFilter(context, facetValues, filter, flow))
        .filter((filter) => filter !== undefined && filter.alias !== undefined)
        .concat([handler.delivery_time(context), handler.gig_price_range(context)])
        .map((filter) => ({ ...filter, type: GLOBAL_FILTERS.includes(filter.id) ? FILTER_TYPES.GLOBAL : FILTER_TYPES.SPECIFIC }))
        .sort(sortByOrderAscending);
};

const buildSubCategoryFilter = (facets, context) => handler[context.flow](context, facets);

const buildSelectedFilter = (sideBar, topBarOrderMap, context) => sideBar.filter(({ id }) => !SELECTED_EXCLUDED.includes(id))
    .reduce((acc, filter) => acc.concat(selectedHandler.handler(filter, context)), [])
    .sort((f1, f2) => topBarOrderMap[f1.id] > topBarOrderMap[f2.id]);

const buildFacetsFromActiveFilters = (activeFilters) => Object.keys(activeFilters)
    .filter((id) => ![FILTER.GIG_PRICE_RANGE.ID, FILTER.DELIVERY_TIME.ID].includes(id))
    .reduce((acc, id) => {
        const filterValue = activeFilters[id];
        acc[id] = Array.isArray(filterValue) ? filterValue.map((val) => ({ id: val })) : [{ id: filterValue }];
        return acc;
    }, {});

/**
 * Build an active filters mapper
 * @param {object} baseActiveFilters - single filters map - type to value
 * @param {object} staticFilters
 * @returns {object} active filters map including multiple filters
 */
const buildActiveFilters = (baseActiveFilters, staticFilters) => Object.entries(baseActiveFilters).reduce((acc, [filterId, filterVal]) => {
    if (PAIR_FILTERS_IDS_FLP.includes(filterId) && typeof filterVal === 'string') {
        acc[filterId] = filterVal.split(STATIC_FILTERS_MAPPING_SEPARATORS.HYPHEN);
    } else if (MULTIPLE_FILTERS_IDS_FLP.includes(filterId) && typeof filterVal === 'string') {
        acc = { ...acc, ...staticFilters.multipleFilters[filterVal] };
    } else {
        acc[filterId] = filterVal;
    }

    return acc;
}, {});

const getMultiPairsSelectValue = (options) => {
    const { fromOptions, toOptions } = options;
    const fromSelectedValue = fromOptions.find(({ selected }) => selected);
    const toSelectedValue = toOptions.find(({ selected }) => selected);

    return [
        fromSelectedValue && fromSelectedValue.id,
        toSelectedValue && toSelectedValue.id
    ];
};

const getValueByViewType = (selectedOptions, display_type) => {
    const singleOption = selectedOptions[0];
    switch (display_type) {
        case DISPLAY_TYPES.MULTI_PAIRS_SELECT:
            return getMultiPairsSelectValue(selectedOptions);

        case DISPLAY_TYPES.CHECKBOX_GROUP:
            return selectedOptions.map(({ id }) => id);

        case DISPLAY_TYPES.INPUT_RANGE:
            return [singleOption.min, singleOption.max];

        case DISPLAY_TYPES.TOGGLE:
        case DISPLAY_TYPES.CHECKBOX:
        case DISPLAY_TYPES.PRO:
            return 'true';

        default:
            return singleOption.id;
    }

};

const getSelectedOptions = (options) => options.filter(({ selected }) => selected);

const proFilterByCookie = (isPro, isBusiness) => (isBusiness && isPro ? { pro: isPro } : {});

const getSelectedOptionValue = (options) => {
    const selectedValue = getSelectedOptions(options);
    return selectedValue[0] ? selectedValue[0].id : undefined;
};

/**
 * generate filters selected values based on if the filter has been selected.
 * @param filters - array of filter objects.
 * @returns object of filter id and the selected value based on the display_type
 */
const getFiltersSelectedValues = (filters) =>
    filters.reduce((acc, { id: filterId, options, display_type }) => {
        if (!options) {
            return acc;
        }

        const isOptionsObject = _.isPlainObject(options);
        const selectedOptions = isOptionsObject ? options : getSelectedOptions(options);

        if (!Object.keys(selectedOptions).length) {
            return acc;
        }

        acc[filterId] = getValueByViewType(selectedOptions, display_type);

        return acc;
    }, {});

module.exports = {
    buildAppFilters,
    buildActiveFilters,
    parseActiveFilters,
    activeFiltersToQueryString,
    getFiltersSelectedValues,
    getSelectedOptionValue,
    proFilterByCookie
};

const reorderFilters = (filters) => filters
    .map((filter) => {
        if (reorderMap[filter.id] !== undefined) {
            filter.order = reorderMap[filter.id];
        }
        if (specificTopFilterReorder[filter.id]) {
            filter.options = specificTopFilterReorder[filter.id](filter.options);
        }

        return filter;
    })
    .sort(sortByOrderAscending);

const reorderMap = {
    [FILTER.DELIVERY_TIME.ID]: 0,
    [FILTER.GIG_PRICE_RANGE.ID]: 1,
    [FILTER.SELLER_LEVEL.ID]: 2,
    [FILTER.PRO.ID]: 3,
    [FILTER.IS_SELLER_ONLINE.ID]: 4
};

const specificTopFilterReorder = {
    [FILTER.SELLER_LOCATION.ID]: (options) => {
        const orderMap = FILTER.SELLER_LOCATION.FIRST_VIEW;
        const firstKeys = Object.keys(orderMap);
        const [filtered, rest] = splitArray(options, (({ id }) => firstKeys.includes(id)));

        return filtered.sort((a, b) => orderMap[a.id] - orderMap[b.id]).concat(rest.sort(sortByAliasAlphabetical));
    }
};

const manipulateTopFilters = (topBarFilters) => topBarFilters.map((filter) => (
    {
        ...filter,
        topGrouping: TOP_GROUPING_MAP[filter.id] || TOP_GROUPING.SERVICE
    }
)).sort((a, b) => a.topGrouping - b.topGrouping);

const removeIfNoResults = (activeFilters, filters) => {
    const deliveryTime = activeFilters[FILTER.DELIVERY_TIME.ID];
    const priceRange = activeFilters[FILTER.GIG_PRICE_RANGE.ID];

    if (!deliveryTime) {
        filters = filters.filter(({ id }) => id !== FILTER.DELIVERY_TIME.ID);
    }

    if (!priceRange) {
        filters = filters.filter(({ id }) => id !== FILTER.GIG_PRICE_RANGE.ID);
    }

    return filters;
};

/**
 * Get all the keys of the active static filters (including multiple filters)
 * @param {object} baseStaticFilterKeys - active keys of single static filters
 * @param {object} staticFilters
 * @returns {Array} active filter keys
 */
const extractActiveStaticFilters = (baseStaticFilterKeys, staticFilters) => {
    const { filters } = staticFilters;
    const multipleFilters = filters.filter((filter) => MULTIPLE_FILTERS_IDS_FLP.includes(filter.id)).shift();
    const singleStaticFilterKeys = baseStaticFilterKeys.filter((item) => !MULTIPLE_FILTERS_IDS_FLP.includes(item));

    if (!multipleFilters.values) {
        return singleStaticFilterKeys;
    }
    const activeStaticFilterKeys = singleStaticFilterKeys.concat(...multipleFilters.values.map((item) => item.filtersTypes));

    return [...new Set(activeStaticFilterKeys)];
};
